import React from "react";
import { useLocation, Link, NavLink } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export function AppSidebar(): JSX.Element {
  const location = useLocation();

  return (
    <Sidebar className="top-[64px] h-[calc(100svh-64px)] bg-surface-container-low border-r border-outline-variant transition-colors" collapsible="icon">
      <SidebarHeader className="px-6 py-8 mb-4">
        <div className="group-data-[collapsible=icon]:hidden">
          <h2 className="font-h3 text-h3 text-on-surface mb-1">DocForge</h2>
          <p className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-wider">Scriptorium Alpha</p>
        </div>
        <div className="hidden group-data-[collapsible=icon]:flex items-center justify-center">
          <span className="font-h3 text-primary">D</span>
        </div>
      </SidebarHeader>

      <div className="px-4 mb-8 group-data-[collapsible=icon]:px-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="w-full bg-primary text-on-primary font-label-caps text-[11px] py-7 px-4 rounded-xl flex items-center justify-center gap-2 hover:brightness-110 transition-all uppercase shadow-lg active:scale-95">
              <Link to="/">
                <span className="material-symbols-outlined">add</span>
                <span className="group-data-[collapsible=icon]:hidden">Create New</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </div>

      <SidebarContent className="px-2">
        <SidebarMenu className="space-y-1">
          {[
            { to: "/", label: "New Draft", icon: "edit_note" },
            { to: "/library", label: "Library", icon: "book_5" },
            { to: "/templates", label: "Templates", icon: "description" },
            { to: "/archives", label: "Archives", icon: "archive" },
            { to: "/collaborators", label: "Collaborators", icon: "group" },
            { to: "/settings", label: "Settings", icon: "settings" },
          ].map((item) => (
            <SidebarMenuItem key={item.to}>
              <SidebarMenuButton 
                asChild 
                isActive={location.pathname === item.to}
                className={cn(
                  "mx-2 flex items-center gap-3 px-4 py-6 transition-all rounded-lg",
                  location.pathname === item.to
                    ? "bg-primary/10 text-primary font-bold shadow-sm"
                    : "text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high"
                )}
              >
                <Link to={item.to}>
                  <span className="material-symbols-outlined text-[22px]">{item.icon}</span>
                  <span className="font-label-caps text-[11px] uppercase tracking-widest">{item.label}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>

        <div className="mt-8 px-4 group-data-[collapsible=icon]:hidden">
           <div className="p-5 rounded-2xl bg-secondary-container/5 border border-outline-variant/30">
              <div className="flex items-center gap-2 mb-2">
                <span className="material-symbols-outlined text-secondary text-[20px]">auto_awesome</span>
                <span className="text-[10px] font-bold text-secondary uppercase tracking-widest brand-monospace">Pro Access</span>
              </div>
              <p className="text-[11px] text-on-surface-variant leading-relaxed mb-4 font-body-md">Unlock high-density editorial AI models and unlimited archives.</p>
              <Link to="/upgrade" className="block w-full py-2.5 text-[10px] font-bold text-center uppercase tracking-widest bg-secondary text-on-secondary rounded-lg hover:brightness-110 transition-all shadow-sm">Upgrade</Link>
           </div>
        </div>
      </SidebarContent>

      <SidebarFooter className="p-2 border-t border-outline-variant mt-auto">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton 
              asChild 
              isActive={location.pathname === "/help"}
              className="text-on-surface-variant hover:text-on-surface mx-2 flex items-center gap-3 px-4 py-6 hover:bg-surface-container-high transition-all rounded-lg"
            >
              <Link to="/help">
                <span className="material-symbols-outlined text-[22px]">help</span>
                <span className="font-label-caps text-[11px] uppercase tracking-widest">Help</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton 
              onClick={() => toast.success('Logged out successfully.')}
              className="text-on-surface-variant hover:text-error mx-2 flex items-center gap-3 px-4 py-6 hover:bg-error-container/10 transition-all rounded-lg w-[calc(100%-1rem)]"
            >
              <span className="material-symbols-outlined text-[22px]">logout</span>
              <span className="font-label-caps text-[11px] uppercase tracking-widest">Logout</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}